import React, { useEffect, useState, useCallback } from "react";
import {
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    Row,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    Table
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { getGeneralPnL, getOperatorDropdown, exportTransactions, getOperatorUsers } from "../../../../helpers/apis";
import { GeneralPnLColumns } from "../../../../Components/Common/columns/PnLCols";
import TransactionTab from "./TransactionTab";

const todayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const yesterdayStr = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const last7DaysStr = () => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const thisMonthStartStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const PnL = () => {
    // Loading States
    const [generalLoading, setGeneralLoading] = useState(false);

    // Filters
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [dateRange, setDateRange] = useState([]);

    // General P&L Data & Params
    const [generalData, setGeneralData] = useState({ summary: {}, history: [], rounds: [], pagination: { total: 0 } });
    const [generalParams, setGeneralParams] = useState({ page: 1, limit: 10 });
    const [generalSummary, setGeneralSummary] = useState({ totalBets: "0", totalPayout: "0", adminPnL: "0", totalRounds: "0" });

    // Operator & User Filter State
    const [operators, setOperators] = useState([]);
    const [operatorId, setOperatorId] = useState("");
    const [userAcNumber, setUserAcNumber] = useState("");
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    // Winners Modal State (kept if GeneralPnL uses it, though mostly for Joker details it seems? 
    // Actually GeneralPnLColumns passed handleShowWinners. If General PnL has winners, we keep it.)
    const [winnersModal, setWinnersModal] = useState(false);
    const [winnersData, setWinnersData] = useState([]);
    const [roundNumber, setRoundNumber] = useState(null);

    // Refresh Loading State
    const [refreshLoading, setRefreshLoading] = useState(false);

    const [gameMode, setGameMode] = useState("all");

    // Fetch General P&L
    const fetchGeneralData = useCallback(async (params = {}) => {
        setGeneralLoading(true);
        try {
            const query = {
                startDate,
                endDate,
                page: params.page || generalParams.page,
                limit: params.limit || generalParams.limit,
                gameType: 'lt3', // Lucky Target 1
                isDownload: params.isDownload,
                operatorId: operatorId || undefined,
                userAcNumber: userAcNumber || undefined,
                gameMode: gameMode !== 'all' ? gameMode : undefined
            };

            const res = await getGeneralPnL(query);
            if (res && res.data) {
                setGeneralData(res.data);

                if (res.data.summary) {
                    setGeneralSummary({
                        totalBets: res.data.summary.totalBetsAmount || 0,
                        totalPayout: res.data.summary.totalPayoutAmount || 0,
                        adminPnL: res.data.summary.adminProfit || 0,
                        totalRounds: res.data.summary.totalRounds || 0
                    });
                }

                // Update params if page changed
                if (params.page || params.limit) {
                    setGeneralParams(prev => ({ ...prev, ...params }));
                }
            } else {
                toast.error("Failed to fetch General P&L data");
            }
        } catch (err) {
            console.error("Error fetching General P&L", err);
            toast.error(err.response?.data?.message || err.message || "Error fetching General P&L");
        } finally {
            setGeneralLoading(false);
        }
    }, [startDate, endDate, generalParams, operatorId, userAcNumber, gameMode]);

    // Initial Fetch
    useEffect(() => {
        fetchGeneralData({ page: 1 });
    }, [operatorId, userAcNumber, startDate, endDate, gameMode]);

    // Fetch Operators for dropdown
    useEffect(() => {
        const fetchOperators = async () => {
            try {
                const res = await getOperatorDropdown();
                if (res && res.data) {
                    setOperators(res.data);
                }
            } catch (err) {
                console.error("Error fetching operators", err);
            }
        };
        fetchOperators();
    }, []);

    // Fetch users for selected operator
    useEffect(() => {
        const fetchUsers = async () => {
            if (!operatorId) {
                setUsers([]);
                setUserAcNumber("");
                return;
            }
            setLoadingUsers(true);
            try {
                const res = await getOperatorUsers(operatorId);
                if (res && res.data) {
                    const userList = res.data.users || (Array.isArray(res.data) ? res.data : []);
                    setUsers(userList);
                } else {
                    setUsers([]);
                }
                setUserAcNumber(""); // Reset user selection when operator changes
            } catch (err) {
                console.error("Error fetching users", err);
                setUsers([]);
                setUserAcNumber("");
            } finally {
                setLoadingUsers(false);
            }
        };
        if (operatorId) fetchUsers();
    }, [operatorId]);

    const handleFilter = () => {
        fetchGeneralData({ page: 1 });
    };

    // Handle refresh
    const handleRefresh = async () => {
        setRefreshLoading(true);
        try {
            await fetchGeneralData({ page: generalParams.page });
        } finally {
            setRefreshLoading(false);
        }
    };

    // Handle Winners Modal (Placeholder if needed by Columns)
    const handleShowWinners = (winners, roundNum) => {
        if (winners && Array.isArray(winners) && winners.length > 0) {
            setWinnersData(winners);
            setRoundNumber(roundNum);
            setWinnersModal(true);
        }
    };

    // Filter Component
    const filterJsx = (
        <div className="d-flex gap-2 align-items-center my-2">
            <Flatpickr
                className="form-control"
                placeholder="Start Date to End Date"
                options={{
                    mode: "range",
                    dateFormat: "Y-m-d"
                }}
                value={dateRange}
                onChange={(selectedDates) => {
                    setDateRange(selectedDates);
                    if (selectedDates.length === 2) {
                        const start = selectedDates[0];
                        const end = selectedDates[1];
                        const formatDate = (d) => {
                            let month = '' + (d.getMonth() + 1);
                            let day = '' + d.getDate();
                            const year = d.getFullYear();
                            if (month.length < 2) month = '0' + month;
                            if (day.length < 2) day = '0' + day;
                            return [year, month, day].join('-');
                        };
                        setStartDate(formatDate(start));
                        setEndDate(formatDate(end));
                    } else if (selectedDates.length === 0) {
                        setStartDate("");
                        setEndDate("");
                    }
                }}
                style={{ width: "210px" }}
            />
            <select
                className="form-select"
                style={{
                    width: "150px"
                }}
                value={
                    startDate === todayStr() && endDate === todayStr() ? "Today" :
                        startDate === yesterdayStr() && endDate === yesterdayStr() ? "Yesterday" :
                            startDate === last7DaysStr() && endDate === todayStr() ? "7 Days" :
                                startDate === thisMonthStartStr() && endDate === todayStr() ? "This Month" : "Custom Range"
                }
                onChange={(e) => {
                    const val = e.target.value;
                    let start = startDate, end = endDate;
                    if (val === "Today") { start = todayStr(); end = todayStr(); }
                    else if (val === "Yesterday") { start = yesterdayStr(); end = yesterdayStr(); }
                    else if (val === "7 Days") { start = last7DaysStr(); end = todayStr(); }
                    else if (val === "This Month") { start = thisMonthStartStr(); end = todayStr(); }
                    else { return; }

                    setStartDate(start);
                    setEndDate(end);
                    setDateRange([new Date(start), new Date(end)]);
                }}
            >
                <option value="Custom Range">Custom Range</option>
                <option value="Today">Today</option>
                <option value="Yesterday">Yesterday</option>
                <option value="7 Days">Last 7 Days</option>
                <option value="This Month">This Month</option>
            </select>
            <select
                className="form-select"
                style={{ width: "200px" }}
                value={operatorId}
                onChange={(e) => setOperatorId(e.target.value)}
            >
                <option value="">All Operators</option>
                {operators.map(op => (
                    <option key={op._id} value={op._id}>{op.username || op.email}</option>
                ))}
            </select>
            <select
                className="form-select"
                style={{
                    width: "200px",
                    display: operatorId ? 'block' : 'none'
                }}
                value={userAcNumber}
                onChange={(e) => setUserAcNumber(e.target.value)}
                disabled={!operatorId || loadingUsers}
            >
                <option value="">{loadingUsers ? "Loading Users..." : "All Users"}</option>
                {users.map(u => (
                    <option key={u._id} value={u.acnumber}>{u.acnumber || u.username || u.email}</option>
                ))}
            </select>
            <select
                className="form-select"
                style={{ width: "160px" }}
                value={gameMode}
                onChange={(e) => {
                    setGameMode(e.target.value);
                    setGeneralParams(prev => ({ ...prev, page: 1 }));
                }}
            >
                <option value="all">All Game Modes</option>
                <option value="easy">Easy Mode</option>
                <option value="hard">Hard Mode</option>
                <option value="more_easy">More Easy Mode</option>
                <option value="medium">Medium Mode</option>
            </select>
            <Button color="primary" onClick={handleFilter}>
                Filter
            </Button>
        </div>
    );

    const handleExport = async () => {
        try {
            const params = {
                gameType: 'lt3',
                startDate,
                endDate,
                operatorId: operatorId || undefined,
                userAcNumber: userAcNumber || undefined,
                export: 'excel'
            };
            const response = await exportTransactions(params);

            if (response && response.data) {
                const { exportData, columns, filename, summary } = response.data;

                // Debug: Log summary to verify it's being received
                console.log("Export Response - Summary:", summary);

                if (!exportData || !Array.isArray(exportData)) {
                    toast.error("No data available to export");
                    return;
                }

                // Create workbook
                const wb = XLSX.utils.book_new();
                const ws = {};

                let currentRow = 0;

                // Add summary section at the top if summary exists
                if (summary && typeof summary === 'object' && Object.keys(summary).length > 0) {
                    console.log("Adding summary to Excel");

                    // Format summary with readable labels
                    const labelMap = {
                        totalBetsAmount: 'Total Bets Amount',
                        totalPayoutAmount: 'Total Payout Amount',
                        totalRounds: 'Total Rounds',
                        adminProfit: 'Admin Profit',
                        profitMargin: 'Profit Margin (%)',
                        gameName: 'Game Name',
                        reportType: 'Report Type'
                    };

                    // Add summary title
                    ws[XLSX.utils.encode_cell({ r: currentRow, c: 0 })] = { t: 's', v: 'SUMMARY' };
                    currentRow++;

                    // Add summary data
                    Object.keys(summary).forEach(key => {
                        const label = labelMap[key] || key;
                        ws[XLSX.utils.encode_cell({ r: currentRow, c: 0 })] = { t: 's', v: label };
                        ws[XLSX.utils.encode_cell({ r: currentRow, c: 1 })] = { t: summary[key] === null || summary[key] === undefined ? 's' : typeof summary[key] === 'number' ? 'n' : 's', v: summary[key] };
                        currentRow++;
                    });

                    // Add empty row separator
                    currentRow++;
                }

                // Add column headers
                columns.forEach((col, colIndex) => {
                    ws[XLSX.utils.encode_cell({ r: currentRow, c: colIndex })] = { t: 's', v: col.header };
                });
                currentRow++;

                // Add transaction data rows
                exportData.forEach(row => {
                    columns.forEach((col, colIndex) => {
                        const value = row[col.key];
                        const cellValue = value !== undefined && value !== null ? value : '';
                        const cellType = typeof cellValue === 'number' ? 'n' : 's';
                        ws[XLSX.utils.encode_cell({ r: currentRow, c: colIndex })] = { t: cellType, v: cellValue };
                    });
                    currentRow++;
                });

                // Set column widths
                const colWidths = columns.map(col => ({ wch: col.width || 15 }));
                ws['!cols'] = colWidths;

                // Set range
                ws['!ref'] = XLSX.utils.encode_range({
                    s: { r: 0, c: 0 },
                    e: { r: currentRow - 1, c: columns.length - 1 }
                });

                // Add worksheet to workbook
                XLSX.utils.book_append_sheet(wb, ws, "Transactions");

                // Download file
                const fileName = filename || 'lt3-transaction.xlsx';
                XLSX.writeFile(wb, fileName);

                toast.success("Export completed successfully");
            } else {
                toast.error("Invalid response from server");
            }
        } catch (err) {
            console.error("Export failed", err);
            toast.error(err.message || "Failed to export transactions");
        }
    };

    const generalSummaryMetrics = [
        { label: "Total Bets", value: generalSummary.totalBets, color: "primary" },
        { label: "Total Payout", value: generalSummary.totalPayout, color: "warning" },
        { label: "Admin P&L", value: generalSummary.adminPnL, color: "success" },
        { label: "Total Rounds", value: generalSummary.totalRounds, color: "info" }
    ];

    return (
        <div className="page-content">
            <Container fluid>
                <div className="page-title-box d-flex align-items-center justify-content-between">
                    <h4 className="mb-0">Transactions (Lucky Target 2)</h4>
                </div>

                <Row className="mt-5 p-0 mb-4">
                    <Col lg={12} className="p-0">
                        <Card>
                            <CardHeader>
                                <h5 className="mb-0">P&L History</h5>
                            </CardHeader>
                            <CardBody>
                                <TransactionTab
                                    summaryMetrics={generalSummaryMetrics}
                                    columns={GeneralPnLColumns(handleShowWinners)}
                                    data={generalData.rounds || generalData.history || []}
                                    loading={generalLoading}
                                    totalRows={generalData.pagination?.total || 0}
                                    params={generalParams}
                                    fetchData={fetchGeneralData}
                                    customFilters={filterJsx}
                                    onExport={handleExport}
                                    onRefresh={handleRefresh}
                                    refreshLoading={refreshLoading}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Winners List Modal */}
                <Modal isOpen={winnersModal} toggle={() => setWinnersModal(!winnersModal)}>
                    <ModalHeader toggle={() => setWinnersModal(!winnersModal)}>
                        Winners
                    </ModalHeader>
                    <ModalBody>
                        {winnersData && winnersData.length > 0 ? (
                            <div className="table-responsive">
                                <Table className="table-sm table-nowrap align-middle table-borderless">
                                    <thead className="table-light">
                                        <tr>
                                            <th scope="col">#</th>
                                            <th scope="col">Email</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {winnersData.map((winner, key) => (
                                            <tr key={key}>
                                                <td>{key + 1}</td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <div className="flex-grow-1">
                                                            <h6 className="mb-0">{winner.email || winner.userId || 'Unknown'}</h6>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        ) : (
                            <div className="text-center py-4">
                                <p className="text-muted">No winners found.</p>
                            </div>
                        )}
                    </ModalBody>
                </Modal>
            </Container>
        </div>
    );
};

export default PnL;
